/**
 * The Services screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/services` (all optional; a default is never written):
 * - `q`: search on the service name.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends services in no defined order, so the
 *   default is Name ascending.
 *
 * In the grouped view the sort applies WITHIN each category section: the rows are sorted
 * first and then grouped, and grouping keeps each section's rows in input order. A service
 * under several categories is sorted into each of them. The sections themselves keep the
 * categories-list order (Uncategorized last).
 */
import type { V2Service } from '@/api/services';
import { assignmentModeLabel } from '@/domain/v2Enums';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';

export const SERVICES_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type ServiceSortKey =
  | 'name'
  | 'bucket'
  | 'staff'
  | 'resources'
  | 'taxable'
  | 'variations'
  | 'status';
export const SERVICE_SORT_KEYS: readonly ServiceSortKey[] = [
  'name',
  'bucket',
  'staff',
  'resources',
  'taxable',
  'variations',
  'status',
];
export const SERVICE_DEFAULT_SORT: SortState<ServiceSortKey> = { key: 'name', dir: 'asc' };

/** Display name for a service (its name, falling back to the id). */
export function serviceName(service: V2Service): string {
  return service.name.length > 0 ? service.name : service.id;
}

/** The lifecycle Status chip label (DNKADM-0140). */
export function serviceStatusLabel(status: V2Service['status']): string {
  if (status === 'DRAFT') return 'Draft';
  if (status === 'INACTIVE') return 'Inactive';
  return 'Active';
}

/**
 * What a service row shows (and sorts by) in each column. `bucketLabel` is the screen's
 * resolver ("Free" / bucket name / "—"); an unresolved bucket ("—") and an unknown
 * variation count sort with the blanks (last).
 */
export function serviceSortValue(
  service: V2Service,
  key: ServiceSortKey,
  bucketLabel: (service: V2Service) => string,
): SortValue {
  switch (key) {
    case 'name':
      return serviceName(service);
    case 'bucket': {
      const label = bucketLabel(service);
      return label === '—' ? null : label;
    }
    case 'staff':
      return assignmentModeLabel(service.staffAssignment);
    case 'resources':
      return assignmentModeLabel(service.resourceAssignment);
    case 'taxable':
      return service.taxable ? 'Yes' : 'No';
    case 'variations':
      return service.variationCount;
    case 'status':
      return serviceStatusLabel(service.status);
  }
}

/** The Services search: the service name. */
export function serviceMatches(service: V2Service, query: string): boolean {
  return matchesSearch(query, [serviceName(service)]);
}
